import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const anchorLinks = [
  { label: '事業内容', href: '#services' },
  { label: '店舗情報', href: '#locations' },
  { label: 'お問い合わせ', href: '#contact' },
  { label: 'よくある質問', href: '#faq' },
  { label: '会社概要', href: '#company' },
];

const pageLinks = [
  { label: '天神屋', href: '/tenjin-ya' },
  { label: 'まんがく', href: '/mangaku' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAnchorClick = (href: string) => {
    setMenuOpen(false);
    if (!isHome) {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const textColor = scrolled ? 'text-gray-700 hover:text-primary' : 'text-white/90 hover:text-white';

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="cursor-pointer flex-shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/2ee58abe-fae6-4328-a0eb-d14c48309594/861b13c5-69c8-4434-a2db-fc523b4fa949_jpg_3.jpg?v=f85ac59d211f16d2022997434bc43b1c"
            alt="株式会社BotaN"
            className="h-12 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {anchorLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleAnchorClick(link.href)}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap relative group ${textColor}`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></span>
            </button>
          ))}
          <div className="w-px h-4 bg-white/30"></div>
          {pageLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap relative group ${textColor}`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></span>
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://lin.ee/OGQq9z6"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#06C755] text-white px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap hover:bg-[#05b34c] transition-colors duration-200 cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-line-fill text-sm"></i></div>
            LINE
          </a>
          <a
            href="tel:092-577-9739"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${scrolled ? 'bg-primary text-white hover:bg-primary-light' : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-xs"></i></div>
            092-577-9739
          </a>
        </div>

        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${scrolled ? 'text-gray-800' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-4 flex flex-col gap-4">
            {anchorLinks.map((link) => (
              <button key={link.href} onClick={() => handleAnchorClick(link.href)} className="text-sm font-medium text-gray-700 hover:text-primary text-left cursor-pointer whitespace-nowrap">
                {link.label}
              </button>
            ))}
            <div className="border-t border-gray-100 pt-3 flex flex-col gap-3">
              {pageLinks.map((link) => (
                <Link key={link.href} to={link.href} onClick={() => setMenuOpen(false)} className="text-sm font-medium text-primary cursor-pointer whitespace-nowrap">
                  {link.label} 専用ページ
                </Link>
              ))}
            </div>
            <a href="https://lin.ee/OGQq9z6" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#06C755] text-white px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap hover:bg-[#05b34c] transition-colors duration-200 cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-line-fill text-sm"></i></div>
              LINE
            </a>
            <a href="tel:092-577-9739" className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap justify-center cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-xs"></i></div>
              092-577-9739
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
